const EARTH_RADIUS_M = 6371000;

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle distance in meters between two { lat, lng } points.
 */
export function haversineMeters(a, b) {
  if (
    !a ||
    !b ||
    typeof a.lat !== 'number' ||
    typeof a.lng !== 'number' ||
    typeof b.lat !== 'number' ||
    typeof b.lng !== 'number'
  ) {
    return null;
  }

  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function formatDistance(meters) {
  if (meters == null || !Number.isFinite(meters)) {
    return null;
  }

  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }

  const km = meters / 1000;
  const rounded = km < 10 ? km.toFixed(1).replace('.', ',') : String(Math.round(km));
  return `${rounded} km`;
}

function hasLockCoordinates(lock) {
  return typeof lock.latitude === 'number' && typeof lock.longitude === 'number';
}

/**
 * Split locks into those with distance from origin and those without coordinates.
 * Items with coordinates are sorted nearest-first and include `distanceMeters`.
 */
export function withDistanceFrom(locks, origin) {
  const withDistance = [];
  const withoutPosition = [];

  for (const lock of locks || []) {
    if (!hasLockCoordinates(lock) || !origin) {
      withoutPosition.push(lock);
      continue;
    }

    const distanceMeters = haversineMeters(origin, {
      lat: lock.latitude,
      lng: lock.longitude
    });

    if (distanceMeters == null) {
      withoutPosition.push(lock);
      continue;
    }

    withDistance.push({ ...lock, distanceMeters });
  }

  withDistance.sort((left, right) => left.distanceMeters - right.distanceMeters);

  return { withDistance, withoutPosition };
}

export function partitionNearbyLocks(locks, origin, radiusM) {
  const { withDistance, withoutPosition } = withDistanceFrom(locks, origin);
  const nearby = withDistance.filter((lock) => lock.distanceMeters <= radiusM);
  const farther = withDistance.filter((lock) => lock.distanceMeters > radiusM);

  return {
    nearby,
    other: [...farther, ...withoutPosition]
  };
}

/**
 * Geo helpers for access-group map zones (GeoJSON Polygon).
 * Coordinates use GeoJSON order: [lng, lat].
 */

/**
 * Ray-casting point-in-polygon.
 * @param {{ lat: number, lng: number }} point
 * @param {Array<[number, number]>} ring - closed or open ring of [lng, lat]
 */
export function pointInPolygon(point, ring) {
  if (!point || !Array.isArray(ring) || ring.length < 3) {
    return false;
  }

  const x = point.lng;
  const y = point.lat;
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Normalize Leaflet latLngs (possibly nested) to a GeoJSON ring [lng, lat][].
 */
export function leafletLatLngsToRing(latLngs) {
  if (!latLngs) {
    return null;
  }

  let ring = latLngs;
  while (Array.isArray(ring) && ring.length === 1 && Array.isArray(ring[0])) {
    ring = ring[0];
  }

  if (!Array.isArray(ring) || ring.length < 3) {
    return null;
  }

  const coords = ring.map((ll) => {
    if (Array.isArray(ll)) {
      return [Number(ll[1] ?? ll[0]), Number(ll[0] ?? ll[1])];
    }
    return [Number(ll.lng), Number(ll.lat)];
  });

  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push([first[0], first[1]]);
  }

  return coords;
}

export function ringToGeoJsonPolygon(ring) {
  if (!ring || ring.length < 4) {
    return null;
  }

  return {
    type: 'Polygon',
    coordinates: [ring]
  };
}

export function geoJsonPolygonToRing(geoJson) {
  if (!geoJson) {
    return null;
  }

  let parsed = geoJson;
  if (typeof geoJson === 'string') {
    try {
      parsed = JSON.parse(geoJson);
    } catch {
      return null;
    }
  }

  const ring = parsed?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) {
    return null;
  }

  return ring;
}

export function serializeMapPolygon(geoJson) {
  if (!geoJson) {
    return null;
  }
  return typeof geoJson === 'string' ? geoJson : JSON.stringify(geoJson);
}

export function parseMapPolygon(value) {
  if (!value) {
    return null;
  }
  if (typeof value === 'object' && value.type === 'Polygon') {
    return value;
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (parsed?.type === 'Polygon' && Array.isArray(parsed.coordinates?.[0])) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

export function locksInsidePolygon(locks, geoJsonOrRing) {
  const ring = Array.isArray(geoJsonOrRing)
    ? geoJsonOrRing
    : geoJsonPolygonToRing(geoJsonOrRing);

  if (!ring) {
    return [];
  }

  return (locks || []).filter((lock) => {
    if (typeof lock.latitude !== 'number' || typeof lock.longitude !== 'number') {
      return false;
    }
    return pointInPolygon({ lat: lock.latitude, lng: lock.longitude }, ring);
  });
}

/**
 * Convert GeoJSON ring [lng, lat][] to Leaflet latLng tuples [lat, lng][].
 */
export function ringToLeafletLatLngs(ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return null;
  }

  return ring.map((coord) => {
    const lng = Number(coord[0]);
    const lat = Number(coord[1]);
    return [lat, lng];
  });
}
